<template>
    <div v-if="show" class="product-modal-overlay">
        <div class="product-modal-container">
            <!-- Header du Modal -->
            <div class="product-modal-header">
                <h3>Modifier le produit</h3>
                <button class="product-modal-close" @click="closeModal">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                </button>
            </div>

            <!-- Contenu du Modal -->
            <div class="product-modal-body">
                <form @submit.prevent="submitForm">
                    <!-- Nom du produit -->
                    <div class="product-form-group">
                        <label for="productName">Nom du produit*</label>
                        <input id="productName" v-model="form.name" type="text" placeholder="Entrez le nom du produit"
                            class="product-form-control" :class="{ 'input-error': validationErrors.name }" required />
                        <span v-if="validationErrors.name" class="product-field-error">{{ validationErrors.name[0]
                        }}</span>
                    </div>

                    <!-- Catégorie avec recherche -->
                    <div class="product-form-group">
                        <label for="productCategory">Catégorie</label>
                        <div class="category-search-container" ref="categoryDropdownContainer">
                            <div class="input-with-icon">
                                <input id="productCategory" v-model="searchCategory" type="text"
                                    placeholder="Rechercher ou sélectionner une catégorie" class="product-form-control"
                                    :class="{ 'input-error': validationErrors.category_id }"
                                    @focus="showCategoryDropdown = true" @input="debouncedFetchCategories" />
                                <span class="input-icon-right" v-if="isSearchingCategories">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round" class="loading-spinner">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <path d="M16 12a4 4 0 1 1-8 0 4 4 0 0 1 8 0z"></path>
                                    </svg>
                                </span>
                                <span class="input-icon-right" v-else
                                    @click="showCategoryDropdown = !showCategoryDropdown">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round">
                                        <polyline points="6 9 12 15 18 9"></polyline>
                                    </svg>
                                </span>
                            </div>
                            <div v-if="showCategoryDropdown" class="category-dropdown">
                                <div v-if="categories.length === 0 && !isSearchingCategories && !showNewCategoryInput"
                                    class="dropdown-empty">
                                    <p>Aucune catégorie trouvée</p>
                                    <button @click="prepareNewCategory" class="add-new-category-btn">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                                            stroke-linecap="round" stroke-linejoin="round">
                                            <line x1="12" y1="5" x2="12" y2="19"></line>
                                            <line x1="5" y1="12" x2="19" y2="12"></line>
                                        </svg>
                                        Créer une nouvelle catégorie
                                    </button>
                                </div>
                                <div v-else-if="isSearchingCategories" class="dropdown-loading">
                                    Recherche en cours...
                                </div>
                                <div v-else-if="showNewCategoryInput" class="new-category-form">
                                    <div class="input-with-btns">
                                        <input type="text" v-model="newCategoryName"
                                            placeholder="Nom de la nouvelle catégorie" class="product-form-control"
                                            ref="newCategoryInput" @keyup.enter="createNewCategory"
                                            @keyup.esc="cancelNewCategory" />
                                        <div class="new-category-actions">
                                            <button @click="cancelNewCategory" class="icon-btn cancel-btn"
                                                :disabled="isCreatingCategory">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                                </svg>
                                            </button>
                                            <button @click="createNewCategory" class="icon-btn confirm-btn"
                                                :disabled="!newCategoryName.trim() || isCreatingCategory">
                                                <span v-if="isCreatingCategory" class="small-loader"></span>
                                                <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                                    <polyline points="20 6 9 17 4 12"></polyline>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <ul v-else class="category-list">
                                    <li v-for="category in filteredCategories" :key="category.id"
                                        @click="selectCategory(category)"
                                        :class="{ 'active': form.category_id === category.id }">
                                        {{ category.name }}
                                    </li>
                                    <li class="create-category" @click="prepareNewCategory">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                                            stroke-linecap="round" stroke-linejoin="round">
                                            <line x1="12" y1="5" x2="12" y2="19"></line>
                                            <line x1="5" y1="12" x2="19" y2="12"></line>
                                        </svg>
                                        Créer une nouvelle catégorie
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <span v-if="validationErrors.category_id" class="product-field-error">
                            {{ validationErrors.category_id[0] }}
                        </span>
                    </div>

                    <!-- Prix et TVA -->
                    <div class="product-form-row">
                        <!-- Prix HT -->
                        <div class="product-form-group">
                            <label for="productPriceExclTax">Prix HT*</label>
                            <div class="product-input-group">
                                <div class="product-input-prefix">FCFA</div>
                                <input id="productPriceExclTax" v-model="form.price_excl_tax" type="number" min="0"
                                    step="50" placeholder="0" class="product-form-control"
                                    :class="{ 'input-error': validationErrors.price_excl_tax }" required />
                            </div>
                            <span v-if="validationErrors.price_excl_tax" class="product-field-error">
                                {{ validationErrors.price_excl_tax[0] }}
                            </span>
                        </div>

                        <!-- Taux de TVA -->
                        <div class="product-form-group">
                            <label for="productTaxRate">Taux de TVA (%)*</label>
                            <div class="product-input-group">
                                <input id="productTaxRate" v-model="form.tax_rate" type="number" min="0" max="100"
                                    step="1" placeholder="18" class="product-form-control"
                                    :class="{ 'input-error': validationErrors.tax_rate }" required />
                                <div class="product-input-suffix">%</div>
                            </div>
                            <span v-if="validationErrors.tax_rate" class="product-field-error">
                                {{ validationErrors.tax_rate[0] }}
                            </span>
                        </div>
                    </div>

                    <!-- Description -->
                    <div class="product-form-group">
                        <label for="productDescription">Description</label>
                        <textarea id="productDescription" v-model="form.description"
                            placeholder="Décrivez votre produit ici..." class="product-form-control"
                            :class="{ 'input-error': validationErrors.description }" rows="3"></textarea>
                        <span v-if="validationErrors.description" class="product-field-error">
                            {{ validationErrors.description[0] }}
                        </span>
                    </div>

                    <!-- Image du produit -->
                    <div class="product-form-group">
                        <label>Image du produit (max: 2 Mo)</label>
                        <div class="product-image-uploader" :class="{ 'has-image': imagePreview || form.image_url }">
                            <div v-if="imagePreview || form.image_url" class="product-image-preview">
                                <img :src="imagePreview || form.image_url" alt="Aperçu de l'image" />
                                <div class="image-overlay">
                                    <span class="image-label">{{ imagePreview ? 'Nouvelle image' : 'Image actuelle'
                                    }}</span>
                                    <button type="button" class="product-image-remove" @click="removeImage">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                                            stroke-linecap="round" stroke-linejoin="round">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <line x1="15" y1="9" x2="9" y2="15"></line>
                                            <line x1="9" y1="9" x2="15" y2="15"></line>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                            <div v-else class="product-image-dropzone">
                                <label for="productImage" class="product-image-label">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round">
                                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                        <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                        <polyline points="21 15 16 10 5 21"></polyline>
                                    </svg>
                                    <span>Glisser une image ou cliquer pour sélectionner</span>
                                    <input type="file" id="productImage" @change="handleImageChange" accept="image/*"
                                        class="product-image-input" />
                                </label>
                            </div>
                        </div>
                        <span v-if="validationErrors.image" class="product-field-error">{{ validationErrors.image[0]
                        }}</span>
                    </div>
                </form>
            </div>

            <!-- Footer fixe -->
            <div class="product-modal-footer">
                <button type="button" class="product-btn product-btn-cancel" @click="closeModal">Annuler</button>
                <button type="button" class="product-btn product-btn-primary" :disabled="isSubmitting"
                    @click="submitForm">
                    <span v-if="isSubmitting" class="product-btn-loader"></span>
                    <span v-else>Mettre à jour</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { useToastStore } from '@/store/modules';
import { categoriesService } from '@/services';
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';

export default {
    name: 'EditProductModal',
    props: {
        show: {
            type: Boolean,
            default: false
        },
        product: {
            type: Object,
            required: true
        },
        categories: {
            type: Array,
            default: () => []
        }
    },
    setup() {

        const toastStore = useToastStore();
        const categoryDropdownContainer = ref(null);
        const showNewCategoryInput = ref(false);
        const newCategoryName = ref('');
        const isCreatingCategory = ref(false);
        const showCategoryDropdown = ref(false);

        // Gestionnaire de clic pour fermer le dropdown
        const handleClickOutside = (event) => {
            if (categoryDropdownContainer.value && !categoryDropdownContainer.value.contains(event.target)) {
                showCategoryDropdown.value = false;
            }
        };

        onMounted(() => {
            document.addEventListener('click', handleClickOutside);
        });

        onBeforeUnmount(() => {
            document.removeEventListener('click', handleClickOutside);

        });

        return {
            toastStore,
            categoryDropdownContainer,
            showNewCategoryInput,
            newCategoryName,
            isCreatingCategory,
            showCategoryDropdown
        };
    },
    data() {
        return {
            form: {
                name: '',
                description: '',
                price_excl_tax: '',
                tax_rate: 18,
                category_id: '',
                image: null,
                image_url: null
            },
            imagePreview: null,
            validationErrors: {},
            isSubmitting: false,
            availableCategories: [],
            searchCategory: '',
            isSearchingCategories: false,
            selectedCategoryName: '',
            fetchCategoriesTimeout: null
        }
    },
    computed: {
        filteredCategories() {
            if (!this.searchCategory.trim()) {
                return this.availableCategories;
            }

            const searchTerm = this.searchCategory.toLowerCase().trim();
            return this.availableCategories.filter(
                category => category.name.toLowerCase().includes(searchTerm)
            );
        }
    },
    watch: {
        show(newVal) {
            if (newVal) {
                this.initForm();
                this.fetchCategories();
                // Empêcher le défilement du corps de la page
                document.body.style.overflow = 'hidden';
            } else {
                // Réactiver le défilement
                document.body.style.overflow = '';
            }
        },
        product: {
            handler(newVal) {
                if (this.show && newVal) {
                    this.initForm();
                }
            },
            deep: true
        }
    },
    methods: {
        initForm() {
            if (!this.product) return;

            // Copier les propriétés du produit existant
            this.form = {
                name: this.product.name || '',
                description: this.product.description || '',
                price_excl_tax: this.product.price_excl_tax || '',
                tax_rate: this.product.tax_rate || 0,
                category_id: this.product.category_id || '',
                image: null, // Nouvelle image (si l'utilisateur en sélectionne une)
                image_url: this.product.image_url || null // Image existante
            };

            // Si une catégorie est sélectionnée, mettre à jour le champ de recherche
            if (this.form.category_id && this.categories.length > 0) {
                const category = this.categories.find(c => c.id === this.form.category_id);
                if (category) {
                    this.selectedCategoryName = category.name;
                    this.searchCategory = category.name;
                }
            } else {
                // Sinon, charger les détails de la catégorie
                this.loadSelectedCategoryDetails();
            }

            // Pré-remplir l'aperçu de l'image existante
            this.imagePreview = this.product.image_url || null;
        },

        // Chargement des détails de la catégorie si déjà sélectionnée
        async loadSelectedCategoryDetails() {
            if (this.form.category_id) {
                try {
                    const response = await categoriesService.getCategory(this.form.category_id);
                    if (response && response.data) {
                        this.selectedCategoryName = response.data.name;
                        this.searchCategory = response.data.name;
                    }
                } catch (error) {
                    console.error('Erreur lors du chargement des détails de la catégorie:', error);
                }
            }
        },

        debouncedFetchCategories() {
            clearTimeout(this.fetchCategoriesTimeout);
            this.fetchCategoriesTimeout = setTimeout(() => {
                this.fetchCategories();
            }, 300);
        },

        prepareNewCategory() {
            this.showNewCategoryInput = true;
            this.newCategoryName = this.searchCategory;
            // Attend le prochain cycle de rendu pour le focus
            nextTick(() => {
                if (this.$refs.newCategoryInput) {
                    this.$refs.newCategoryInput.focus();
                }
            });
        },

        cancelNewCategory() {
            this.showNewCategoryInput = false;
            this.newCategoryName = '';
        },

        async createNewCategory() {
            if (!this.newCategoryName.trim()) return;

            this.isCreatingCategory = true;

            try {
                // Appel API pour créer une catégorie
                const response = await categoriesService.createCategory({
                    name: this.newCategoryName.trim()
                });

                const newCategory = response.data.category || response.data;

                // Ajouter la nouvelle catégorie à la liste
                this.availableCategories.push(newCategory);

                // Sélectionner la nouvelle catégorie
                this.form.category_id = newCategory.id;
                this.selectedCategoryName = newCategory.name;
                this.searchCategory = newCategory.name;

                this.toastStore.addToast({
                    type: 'success',
                    message: 'Catégorie créée avec succès!'
                });

                // Fermer l'input de création
                this.showNewCategoryInput = false;
                this.newCategoryName = '';
                this.showCategoryDropdown = false;

            } catch (error) {
                console.error('Erreur lors de la création de la catégorie:', error);
                this.toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors de la création de la catégorie.',
                    duration: 5000
                });
            } finally {
                this.isCreatingCategory = false;
            }
        },

        async fetchCategories() {
            this.isSearchingCategories = true;
            try {
                const response = await categoriesService.getCategories(
                    {
                        perPage: 20,
                        page: 1,
                        search: this.searchCategory
                    }
                );
                if (response && response.data) {
                    if (response.data.data) {
                        this.availableCategories = response.data.data;
                    } else {
                        this.availableCategories = response.data;
                    }
                }
            } catch (error) {
                console.error('Erreur lors de la récupération des catégories:', error);
                this.toastStore.addToast({
                    type: 'error',
                    message: 'Une erreur est survenue lors de la récupération des catégories.',
                    duration: 6000
                });
            } finally {
                this.isSearchingCategories = false;
            }
        },

        selectCategory(category) {
            this.form.category_id = category.id;
            this.selectedCategoryName = category.name;
            this.searchCategory = category.name;
            this.showCategoryDropdown = false;
        },

        handleImageChange(event) {
            const file = event.target.files[0];
            if (!file) return;

            // Vérifier la taille du fichier (max 2MB)
            if (file.size > 2 * 1024 * 1024) {
                this.validationErrors.image = ['L\'image ne doit pas dépasser 2 Mo'];
                return;
            }

            this.form.image = file;
            this.validationErrors.image = null;

            // Créer un aperçu de l'image
            const reader = new FileReader();
            reader.onload = (e) => {
                this.imagePreview = e.target.result;
            };
            reader.readAsDataURL(file);
        },

        removeImage() {
            // Si c'est une nouvelle image (imagePreview), on la supprime
            if (this.imagePreview) {
                this.form.image = null;
                this.imagePreview = null;
            } else {
                // Si c'est l'image existante, on la marque pour suppression
                this.form.image_url = null;
                this.imagePreview = null;
            }
        },

        validateForm() {
            let isValid = true;
            this.validationErrors = {};

            // Validation du nom
            if (!this.form.name) {
                this.validationErrors.name = ['Le nom du produit est requis'];
                isValid = false;
            } else if (this.form.name.length > 255) {
                this.validationErrors.name = ['Le nom ne doit pas dépasser 255 caractères'];
                isValid = false;
            }

            // Validation du prix HT
            if (!this.form.price_excl_tax) {
                this.validationErrors.price_excl_tax = ['Le prix HT est requis'];
                isValid = false;
            } else if (isNaN(parseFloat(this.form.price_excl_tax)) || parseFloat(this.form.price_excl_tax) < 0) {
                this.validationErrors.price_excl_tax = ['Le prix doit être un nombre positif'];
                isValid = false;
            }

            // Validation du taux de TVA
            if (this.form.tax_rate === '' || this.form.tax_rate === null) {
                this.validationErrors.tax_rate = ['Le taux de TVA est requis'];
                isValid = false;
            } else if (
                isNaN(parseFloat(this.form.tax_rate)) ||
                parseFloat(this.form.tax_rate) < 0 ||
                parseFloat(this.form.tax_rate) > 100
            ) {
                this.validationErrors.tax_rate = ['Le taux de TVA doit être un nombre entre 0 et 100'];
                isValid = false;
            }

            return isValid;
        },

        submitForm() {
            if (!this.validateForm()) {
                // Faire défiler jusqu'à la première erreur
                const firstErrorEl = document.querySelector('.input-error');
                if (firstErrorEl) {
                    firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
                return;
            }

            this.isSubmitting = true;

            // Créer une copie du formulaire pour éviter les mutations directes
            const formData = {
                ...this.form,
                // Indiquer si l'image existante doit être supprimée
                remove_existing_image: !this.form.image_url && !this.imagePreview
            };

            // Émettre l'événement avec les données du formulaire
            this.$emit('submit', formData);

            // Note: isSubmitting sera réinitialisé par le parent via la modification de 'show'
        },

        closeModal() {
            this.$emit('close');
        }
    }
}
</script>

<style scoped>
/* Modal de base */
.product-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
    padding: 20px;
    overflow-y: auto;
}

.product-modal-container {
    width: 100%;
    max-width: 700px;
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 40px);
    position: relative;
    animation: modal-appear 0.3s ease-out;
}

@keyframes modal-appear {
    from {
        opacity: 0;
        transform: translateY(40px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* En-tête du modal */
.product-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid #e2e8f0;
    position: sticky;
    top: 0;
    background-color: white;
    z-index: 10;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
}

.product-modal-header h3 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #0A5C6B;
    line-height: 1.4;
}

.product-modal-close {
    background: none;
    border: none;
    color: #64748b;
    cursor: pointer;
    padding: 8px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}

.product-modal-close:hover {
    background-color: #f1f5f9;
    color: #0A5C6B;
}

/* Corps du modal */
.product-modal-body {
    padding: 24px;
    overflow-y: auto;
    max-height: calc(100vh - 170px);
}

/* Formulaire */
.product-form-group {
    margin-bottom: 20px;
}

.product-form-row {
    display: flex;
    gap: 16px;
    margin-bottom: 20px;
}

.product-form-row .product-form-group {
    flex: 1;
    margin-bottom: 0;
}

label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    color: #334155;
    font-size: 15px;
}

.product-form-control {
    width: 100%;
    padding: 12px 14px;
    font-size: 15px;
    color: #1e293b;
    background-color: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    transition: all 0.2s ease;
}

.product-form-control:focus {
    background-color: white;
    border-color: #0A5C6B;
    box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.15);
    outline: none;
}

.product-form-control::placeholder {
    color: #94a3b8;
}

.input-error {
    border-color: #f87171 !important;
}

.product-field-error {
    display: block;
    color: #ef4444;
    font-size: 13px;
    margin-top: 6px;
}

/* Dropdown pour les catégories */
.category-search-container {
    position: relative;
    width: 100%;
}

.input-with-icon {
    position: relative;
    width: 100%;
}

.input-icon-right {
    position: absolute;
    top: 50%;
    right: 14px;
    transform: translateY(-50%);
    color: #64748b;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}

.loading-spinner {
    animation: rotate 1s linear infinite;
}

@keyframes rotate {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.category-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    max-height: 250px;
    overflow-y: auto;
    background-color: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 20;
    margin-top: 4px;
}

.dropdown-empty,
.dropdown-loading {
    padding: 12px 16px;
    text-align: center;
    color: #64748b;
    font-size: 14px;
}

.category-list {
    list-style: none;
    margin: 0;
    padding: 0;
}

.category-list li {
    padding: 10px 16px;
    color: #1e293b;
    font-size: 14px;
    cursor: pointer;
    transition: background-color 0.2s;
}

.category-list li:hover {
    background-color: #f8fafc;
}

.category-list li.active {
    background-color: #e0f2fe;
    color: #0A5C6B;
    font-weight: 500;
}

.category-list li.create-category {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #0A5C6B;
    font-weight: 500;
    border-top: 1px solid #e2e8f0;
    margin-top: 5px;
}

.category-list li.create-category:hover {
    background-color: #e0f2fe;
}

.add-new-category-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin: 10px auto;
    padding: 8px 12px;
    background-color: #e0f2fe;
    color: #0A5C6B;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
}

.add-new-category-btn:hover {
    background-color: #bfdbfe;
}

.new-category-form {
    padding: 10px;
}

.input-with-btns {
    display: flex;
    align-items: center;
    gap: 8px;
}

.new-category-actions {
    display: flex;
    gap: 5px;
}

.icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 4px;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
}

.icon-btn.cancel-btn {
    background-color: #f1f5f9;
    color: #64748b;
}

.icon-btn.cancel-btn:hover:not(:disabled) {
    background-color: #e2e8f0;
    color: #475569;
}

.icon-btn.confirm-btn {
    background-color: #0A5C6B;
    color: white;
}

.icon-btn.confirm-btn:hover:not(:disabled) {
    background-color: #094854;
}

.icon-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.small-loader {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.5);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

/* Groupe d'entrée avec préfixe/suffixe */
.product-input-group {
    display: flex;
    align-items: stretch;
}

.product-input-prefix,
.product-input-suffix {
    display: flex;
    align-items: center;
    padding: 0 14px;
    background-color: #f1f5f9;
    color: #64748b;
    font-weight: 500;
    border: 1px solid #e2e8f0;
}

.product-modal-footer {
    display: flex;
    justify-content: space-between;
    padding: 20px 24px;
    border-top: 1px solid #e2e8f0;
    position: sticky;
    bottom: 0;
    background-color: white;
    z-index: 10;
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
}

.product-input-prefix {
    border-right: none;
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
}

.product-input-suffix {
    border-left: none;
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
}

.product-input-group .product-form-control {
    flex: 1;
}

.product-input-group .product-form-control:first-child {
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
}

.product-input-group .product-form-control:last-child {
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
}

.product-input-prefix+.product-form-control {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
}

.product-form-control+.product-input-suffix {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
}

/* Upload d'image */
.product-image-uploader {
    border: 2px dashed #cbd5e1;
    border-radius: 10px;
    overflow: hidden;
    transition: all 0.2s ease;
}

.product-image-uploader:hover {
    border-color: #0A5C6B;
}

.product-image-uploader.has-image {
    border-style: solid;
}

.product-image-dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 30px 20px;
    text-align: center;
    cursor: pointer;
}

.product-image-label {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    color: #64748b;
    cursor: pointer;
    width: 100%;
}

.product-image-label svg {
    color: #94a3b8;
}

.product-image-label span {
    font-size: 14px;
}

.product-image-input {
    position: absolute;
    width: 0;
    height: 0;
    opacity: 0;
}

.product-image-preview {
    position: relative;
    width: 100%;
    padding-top: 56.25%;
    /* 16:9 aspect ratio */
    background-color: #f8fafc;
}

.product-image-preview img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0.7) 0%, transparent 30%, transparent 70%, rgba(0, 0, 0, 0.7) 100%);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 12px;
    opacity: 0;
    transition: opacity 0.3s ease;
}

.product-image-preview:hover .image-overlay {
    opacity: 1;
}

.image-label {
    color: white;
    font-size: 12px;
    font-weight: 500;
    background: rgba(0, 0, 0, 0.6);
    padding: 4px 8px;
    border-radius: 4px;
    align-self: flex-start;
}

.product-image-remove {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 32px;
    height: 32px;
    background-color: rgba(255, 255, 255, 0.9);
    border: none;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #ef4444;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
}

.product-image-remove:hover {
    background-color: white;
    transform: scale(1.1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* Boutons d'action */
.product-form-actions {
    display: flex;
    justify-content: space-between;
    margin-top: 30px;
    padding-top: 20px;
    border-top: 1px solid #e2e8f0;
}

.product-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 46px;
    padding: 0 24px;
    font-size: 15px;
    font-weight: 500;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 120px;
}

.product-btn-primary {
    background-color: #0A5C6B;
    color: white;
    border: none;
}

.product-btn-primary:hover:not(:disabled) {
    background-color: #094854;
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(9, 72, 84, 0.2);
}

.product-btn-primary:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: none;
}

.product-btn-cancel {
    background-color: white;
    color: #475569;
    border: 1px solid #cbd5e1;
}

.product-btn-cancel:hover {
    background-color: #f8fafc;
    color: #334155;
}

.product-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

/* Animation du loader de bouton */
.product-btn-loader {
    display: inline-block;
    width: 20px;
    height: 20px;
    border: 2px solid rgba(255, 255, 255, 0.5);
    border-radius: 50%;
    border-top-color: white;
    animation: button-spin 0.8s linear infinite;
}

@keyframes button-spin {
    to {
        transform: rotate(360deg);
    }
}

/* Responsive */
@media (max-width: 640px) {
    .product-form-row {
        flex-direction: column;
        gap: 20px;
    }

    .product-modal-container {
        max-height: 90vh;
    }

    .product-modal-body {
        max-height: calc(90vh - 120px);
    }
}
</style>
